import { useQuery } from '@tanstack/react-query';
import {
  GetProjectWithSubProjectsQuery,
  GetProjectWithSubProjectsQueryVariables,
  ProjectPickerDetailsQuery,
  ProjectPickerDetailsQueryVariables,
  ProjectPickerFlatQuery,
  ProjectPickerFlatQueryVariables,
  ProjectSearchFields,
  SearchType,
  SortOrder,
  type CostCode,
} from '__generated__/graphql';
import { ArchivedStatus } from 'components/domain/archived/ArchivedPicker/ArchivedPicker';
import { useDebounce } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { cloneDeep, isEmpty, keyBy, sortBy } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { Optional } from 'types/util/Optional';
import { getGraphQlContainsComparison, getGraphQlDoesNotContainComparison } from 'utils/apolloUtils';
import { logError } from 'utils/errorUtils';
import { projectFlatQuery, projectPickerDetails, subProjectQuery } from '../project-picker-queries';

export type ProjectPickerItem = NonNullArrayField<ProjectPickerDetailsQuery['projects']>;

export type ProjectPickerSubItem = NonNullArrayField<
  NonNullArrayField<GetProjectWithSubProjectsQuery['projects']>['children']
>;

// Minimal shape needed to track drill-in layers (satisfied by both ProjectPickerItem and ProjectPickerSubItem)
export type ProjectPickerLayer = {
  id: string;
  title: string;
  archivedOn?: string | null;
  children?: Array<{ id: string; archivedOn?: string | null }> | null;
  ancestors?: Array<{ id: string; title: string; depth?: number | null }> | null;
};

export interface UseProjectPickerDataOptions {
  blacklistedIds?: string[];
  archivedStatus?: ArchivedStatus;
  topLevelOnly?: boolean;
  scopeToCostCodeIds?: Array<CostCode['id']>;
  pageSize?: number;
  queryKeyPrefix: string;
  onProjectsLoaded?: (projects: ProjectPickerItem[]) => void;
  onSubProjectsLoaded?: (parentId: string, children: ProjectPickerSubItem[]) => void;
}

export function useProjectPickerData({
  blacklistedIds,
  archivedStatus,
  topLevelOnly,
  scopeToCostCodeIds,
  pageSize = 10,
  queryKeyPrefix,
  onProjectsLoaded,
  onSubProjectsLoaded,
}: UseProjectPickerDataOptions) {
  const [searchValue, setSearchValue] = useState('');
  const [layers, setLayers] = useState<ProjectPickerLayer[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [focusStartId, setFocusStartId] = useState<string | null>(null);
  const savedScrollPositions = useRef<number[]>([]);
  const isRestoringScroll = useRef(false);

  const graphqlClient = useGraphQLClient();
  const graphqlPaging = useGraphQLPaging();
  const baseQueryKey = useReactQueryBaseKey();
  const searchDebounce = useDebounce(searchValue, 250);
  const scroller = useRef<HTMLElement>();

  const isDrilledIn = layers.length > 0;
  const hasSearch = !!searchDebounce && searchDebounce.length > 0;

  const variables: ProjectPickerFlatQueryVariables = useMemo(
    () => ({
      filter: {
        id: getGraphQlDoesNotContainComparison(blacklistedIds),
        archivedOn:
          archivedStatus === 'all' ? undefined : archivedStatus === 'archived' ? { isNull: false } : { isNull: true },
        parentProjectId: topLevelOnly || !hasSearch ? { isNull: true } : undefined,
        projectCostCodeLink: scopeToCostCodeIds
          ? { costCodeId: getGraphQlContainsComparison(scopeToCostCodeIds) }
          : undefined,
        search:
          hasSearch && !isDrilledIn
            ? {
                type: SearchType.Contains,
                value: searchDebounce,
                fields: [ProjectSearchFields.Title, ProjectSearchFields.Number],
              }
            : undefined,
      },
      sort: !hasSearch ? [{ title: SortOrder.Asc }] : undefined,
    }),
    [blacklistedIds, hasSearch, isDrilledIn, scopeToCostCodeIds, archivedStatus, searchDebounce, topLevelOnly]
  );

  async function loadProjects(after?: Optional<string>): Promise<ProjectPickerItem[]> {
    try {
      let projects: ProjectPickerItem[];

      if (hasSearch) {
        const flat = await graphqlPaging<ProjectPickerFlatQuery, ProjectPickerFlatQueryVariables>(
          { document: projectFlatQuery, variables: { after, ...variables } },
          'projects',
          500
        );
        const ids = flat.map((p) => p.id);
        if (isEmpty(ids)) {
          return [];
        }
        const details = await graphqlPaging<ProjectPickerDetailsQuery, ProjectPickerDetailsQueryVariables>(
          { document: projectPickerDetails, variables: { filter: { id: getGraphQlContainsComparison(ids) } } },
          'projects',
          500
        );
        projects = sortBy(details, ({ depth, title }) => [depth !== 1, title?.toLocaleLowerCase()]);
      } else {
        const flatResult = await graphqlClient.request<ProjectPickerFlatQuery, ProjectPickerFlatQueryVariables>({
          document: projectFlatQuery,
          variables: { after, first: pageSize, ...variables },
        });
        const grouped = keyBy(flatResult.projects, 'id');
        const ids = Object.keys(grouped);
        if (isEmpty(ids)) {
          return [];
        }
        const detailResult = await graphqlClient.request<ProjectPickerDetailsQuery, ProjectPickerDetailsQueryVariables>(
          {
            document: projectPickerDetails,
            variables: { first: pageSize, filter: { id: getGraphQlContainsComparison(ids) } },
          }
        );
        const withCursor = detailResult.projects?.map((p) => ({ ...p, cursor: grouped[p.id]?.cursor }));
        projects = sortBy(withCursor, ({ title }) => title?.toLocaleLowerCase());
      }

      onProjectsLoaded?.(projects);
      return projects;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async function fetchSelected(ids: string[]): Promise<ProjectPickerItem[]> {
    return graphqlPaging<ProjectPickerDetailsQuery, ProjectPickerDetailsQueryVariables>(
      {
        document: projectPickerDetails,
        variables: { filter: { id: getGraphQlContainsComparison(ids) } },
      },
      'projects',
      500
    );
  }

  async function loadSubProjectSheet(): Promise<ProjectPickerSubItem[]> {
    try {
      const parentId = layers[layers.length - 1].id;
      const result = await graphqlPaging<GetProjectWithSubProjectsQuery, GetProjectWithSubProjectsQueryVariables>(
        { document: subProjectQuery, variables: { projectId: parentId, first: pageSize } },
        'projects'
      );
      const children = sortBy(
        (result[0]?.children ?? []).filter((c) => c.archivedOn === null),
        (c) => c.title?.toLocaleLowerCase()
      ) as ProjectPickerSubItem[];
      onSubProjectsLoaded?.(parentId, children);
      return children;
    } catch (e) {
      logError(e);
      throw e;
    }
  }

  const {
    data: nonSearchData,
    isLoading: nonSearchLoading,
    isError: nonSearchError,
    loadedAll,
  } = useReactQueryLazyLoading(scroller.current, [...baseQueryKey, queryKeyPrefix, variables], loadProjects, pageSize, {
    enabled: isOpen && !isDrilledIn && !hasSearch,
    refetchOnWindowFocus: false,
  });

  const {
    data: searchData,
    isLoading: searchLoading,
    isError: searchError,
  } = useQuery({
    queryKey: [...baseQueryKey, `${queryKeyPrefix}-search`, variables],
    queryFn: () => loadProjects(),
    enabled: isOpen && hasSearch && !isDrilledIn,
    refetchOnWindowFocus: false,
  });

  const {
    data: subProjectData,
    isLoading: isSubLoading,
    isError: isSubError,
  } = useQuery({
    queryKey: [...baseQueryKey, `${queryKeyPrefix}-sub-projects`, layers[layers.length - 1]?.id],
    queryFn: loadSubProjectSheet,
    enabled: isOpen && isDrilledIn,
    refetchOnWindowFocus: false,
  });

  const data = hasSearch ? searchData : nonSearchData;
  const isLoading = hasSearch ? searchLoading : nonSearchLoading;
  const isDataError = hasSearch ? searchError : nonSearchError;

  function openPicker(focusId?: string | null) {
    setIsOpen(true);
    if (focusId !== undefined) {
      setFocusStartId(focusId);
    }
  }

  function closePicker() {
    setIsOpen(false);
    setSearchValue('');
    setLayers([]);
    setFocusStartId(null);
    savedScrollPositions.current = [];
    isRestoringScroll.current = false;
  }

  function goBack() {
    const leaving = layers[layers.length - 1];
    isRestoringScroll.current = true;
    setLayers((prev) => {
      const next = cloneDeep(prev);
      next.pop();
      return next;
    });
    if (leaving) {
      setTimeout(() => {
        setFocusStartId(leaving.id);
      });
    }
  }

  function drillInto(project: ProjectPickerLayer) {
    savedScrollPositions.current.push(scroller.current?.scrollTop ?? 0);
    isRestoringScroll.current = false;
    setFocusStartId(null);
    setLayers((prev) => [...prev, project]);
  }

  function registerScroller(ref: HTMLElement) {
    scroller.current = ref;
    if (isRestoringScroll.current && savedScrollPositions.current.length > 0) {
      const top = savedScrollPositions.current.pop()!;
      isRestoringScroll.current = false;
      setTimeout(() => {
        if (scroller.current) {
          scroller.current.scrollTop = top;
        }
      });
    }
  }

  return {
    openPicker,
    closePicker,
    goBack,
    drillInto,
    registerScroller,
    searchValue,
    setSearchValue,
    searchDebounce,
    layers,
    setLayers,
    focusStartId,
    isDrilledIn,
    data,
    isLoading,
    isDataError,
    loadedAll,
    subProjectData,
    isSubLoading,
    isSubError,
    fetchSelected,
  };
}
